// Template215: Osaka - Excursiones desde Osaka Nara/Kobe (Page12)
// Nara portada, intro, Parque ciervos, Todai-ji, Kobe, Cómo llegar.

import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";

const Template215 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const headerText = page?.texts?.[0]?.content;
	const naraTitle = page?.texts?.[1]?.content;
	const intro = page?.texts?.[2]?.content;
	const ciervosTitle = page?.texts?.[3]?.content;
	const ciervosBody = page?.texts?.[4]?.content;
	const todaijiTitle = page?.texts?.[5]?.content;
	const todaijiBody = page?.texts?.[6]?.content;
	const kobeTitle = page?.texts?.[7]?.content;
	const kobeBody = page?.texts?.[8]?.content;
	const kobeTip = page?.texts?.[9]?.content;
	const llegarTitle = page?.texts?.[10]?.content;
	const llegarNara = page?.texts?.[11]?.content;
	const llegarKobe = page?.texts?.[12]?.content;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found?.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found?.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const naraImage = getImageSrc("nara");
	const deerImage = getImageSrc("deer");
	const todaijiImage = getImageSrc("todaiji");
	const kobeImage = getImageSrc("kobe");
	const logoImage = getCanvasImageUrl("logo");

	const sabiasQue = Array.isArray(page?.data?.sabiasQue)
		? page.data.sabiasQue
		: [];

	const actualHeaderColor = headerBackgroundColor || "#1F4E3D";

	const contentColor = textColor || "#444";

	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 14,
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
		marginBottom: 0.5,
		color: contentColor,
	};

	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 13,
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontSize: "14px",
					fontWeight: 600,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 1,
				}}
			>
				{headerText}
				{logoImage && (
					<Box
						component="img"
						src={logoImage}
						alt="logo"
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			{naraImage && (
				<Box data-block-id="image-0" sx={{ position: "relative" }}>
					<Box
						component="img"
						src={naraImage}
						alt=""
						sx={{ width: "100%", height: 150, objectFit: "cover" }}
					/>
					{naraTitle && (
						<Box
							component="h1"
							sx={{
								fontSize: 28,
								position: "absolute",
								bottom: 0,
								left: 20,
								color: "#fff",
								textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
								margin: 1.25,
							}}
						>
							{naraTitle}
						</Box>
					)}
				</Box>
			)}

			<Box data-block-id="text-1" sx={{ padding: "10px 12px" }}>
				{intro && (
					<Box
						sx={{ fontSize: 13, color: "#333", margin: 0, marginBottom: 1.25 }}
					>
						{intro}
					</Box>
				)}

				<Box
					data-block-id="text-2"
					sx={{
						display: "flex",
						gap: 2,
						marginBottom: 1.5,
						position: "relative",
					}}
				>
					{deerImage && (
						<Box
							component="img"
							src={deerImage}
							alt=""
							sx={{
								width: "40%",
								height: 90,
								borderRadius: "12px",
								objectFit: "cover",
							}}
						/>
					)}
					<Box sx={{ width: "60%" }}>
						{ciervosTitle && (
							<Box component="h3" sx={estiloH3}>
								{ciervosTitle}
							</Box>
						)}
						{ciervosBody && (
							<Box component="p" sx={estiloP}>
								{ciervosBody}
							</Box>
						)}
					</Box>
					{sabiasQue.length > 0 && (
						<>
							<Box sx={{ position: "absolute", top: -10, left: "33%" }}>
								<SabiasQue
									icono={sabiasQue[0]?.icono ?? "🦌"}
									texto={sabiasQue[0]?.texto ?? ""}
								/>
							</Box>
							{sabiasQue[1] && (
								<Box sx={{ position: "absolute", top: 40, left: "33%" }}>
									<SabiasQue
										icono={sabiasQue[1]?.icono ?? "⚠️"}
										texto={sabiasQue[1]?.texto ?? ""}
									/>
								</Box>
							)}
						</>
					)}
				</Box>

				<Box
					data-block-id="text-3"
					sx={{
						display: "flex",
						gap: 2,
						marginBottom: 1.5,
						position: "relative",
					}}
				>
					{todaijiImage && (
						<Box
							component="img"
							src={todaijiImage}
							alt=""
							sx={{
								width: "40%",
								height: 90,
								borderRadius: "12px",
								objectFit: "cover",
							}}
						/>
					)}
					<Box sx={{ width: "60%" }}>
						{todaijiTitle && (
							<Box component="h3" sx={estiloH3}>
								{todaijiTitle}
							</Box>
						)}
						{todaijiBody && (
							<Box component="p" sx={estiloP}>
								{todaijiBody}
							</Box>
						)}
					</Box>
					{sabiasQue[2] && (
						<Box sx={{ position: "absolute", top: -10, left: "33%" }}>
							<SabiasQue
								icono={sabiasQue[2]?.icono ?? "🏛️"}
								texto={sabiasQue[2]?.texto ?? ""}
							/>
						</Box>
					)}
				</Box>
			</Box>

			{/* Kobe */}
			<Box
				data-block-id="text-4"
				sx={{
					backgroundColor: "#d1e6de",
					padding: 1.5,
					margin: "0 12px",
					borderRadius: "12px",
					position: "relative",
				}}
			>
				{kobeImage && (
					<Box
						component="img"
						src={kobeImage}
						alt=""
						sx={{
							width: 95,
							position: "absolute",
							bottom: isSmallScreen ? -200 : -80,
							right: 0,
						}}
					/>
				)}
				{kobeTip && (
					<Box
						sx={{
							position: "absolute",
							bottom: isSmallScreen ? -180 : -30,
							right: 80,
							backgroundColor: "#fff8db",
							border: "2px solid #f5c400",
							borderRadius: "16px 16px 0px 16px",
							padding: "10px 12px",
							fontSize: 11,
							color: contentColor,
							boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
							maxWidth: 190,
						}}
					>
						{kobeTip}
					</Box>
				)}
				{kobeTitle && (
					<Box
						component="h2"
						sx={{
							fontFamily: "inherit",
							fontSize: 16,
							fontWeight: 700,
							margin: 0,
							marginBottom: 1,
							color: contentColor,
						}}
					>
						{kobeTitle}
					</Box>
				)}
				{kobeBody && (
					<Box component="p" sx={{ ...estiloP, marginBottom: 0.75 }}>
						{kobeBody}
					</Box>
				)}
			</Box>

			{(llegarTitle || llegarNara || llegarKobe) && (
				<Box data-block-id="text-5" sx={{ padding: "12px" }}>
					<Box
						sx={{
							backgroundColor: "#d1e6de",
							border: `2px solid ${actualHeaderColor}`,
							borderRadius: "12px",
							padding: "12px 16px",
							fontSize: 12,
						}}
					>
						{llegarTitle && (
							<Box
								component="h4"
								sx={{
									margin: 0,
									marginBottom: 1,
									fontSize: 13,
									fontWeight: 700,
									color: contentColor,
									textTransform: "uppercase",
								}}
							>
								{llegarTitle}
							</Box>
						)}
						<Box sx={{ display: "flex", gap: 2.5 }}>
							{llegarNara && (
								<Box sx={{ flex: 1 }}>
									<Box component="p" sx={{ ...estiloP, fontWeight: 700 }}>
										Nara:
									</Box>
									<Box component="p" sx={estiloP}>
										{llegarNara}
									</Box>
								</Box>
							)}
							{llegarKobe && (
								<Box sx={{ flex: 1 }}>
									<Box component="p" sx={{ ...estiloP, fontWeight: 700 }}>
										Kobe:
									</Box>
									<Box component="p" sx={estiloP}>
										{llegarKobe}
									</Box>
								</Box>
							)}
						</Box>
					</Box>
				</Box>
			)}
		</Box>
	);
};

Template215.thumbnail = "/images/template-1.png";
export default Template215;
